import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { API, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignaturePad } from "@/components/SignaturePad";
import { ExtraItemsList, countsText } from "@/components/ServiceReport";
import { CheckCircle2, XCircle, FileText, PenLine } from "lucide-react";

export default function ProposalApproval() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [signer, setSigner] = useState("");
  const [signature, setSignature] = useState("");
  useEffect(() => { api.get(`/public/report/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(apiErr(e))); }, [token]);
  const decide = async (decision) => {
    if (decision === "approved" && !signature) { setErr("Ke schválení prosím připojte svůj podpis."); return; }
    setBusy(true); setErr("");
    try { await api.post(`/public/report/${token}/decision`, { decision, comment: "", signer_name: signer, signature }); const r = await api.get(`/public/report/${token}`); setInfo(r.data); }
    catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };
  const pdf = `${API}/public/report/${token}/zapis.pdf`;
  const rep = info?.report || {};
  const preset = sp.get("d");
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-start justify-center p-4 pt-10">
      <div className="w-full max-w-lg bg-white rounded-2xl border shadow-sm p-6 space-y-4" data-testid="approval-page">
        <div className="okenfix-brand text-2xl font-extrabold" style={{ color: "#C5A059" }}>OKENFIX</div>
        {err && <p className="text-red-600" data-testid="approval-error">{err}</p>}
        {info && (
          <>
            <h1 className="text-xl font-bold text-[#0A2540]">Zápis ze servisu – {info.svj_name}</h1>
            <p className="text-slate-600">Vchod {info.entrance} · byt <b>{info.apartment}</b> · {info.date}</p>
            <div className="bg-slate-50 rounded-lg p-3 text-sm" data-testid="approval-standard">Provedeno dle smlouvy: kompletní servis – <b>{countsText(rep)}</b>{rep.other_work ? ` · ${rep.other_work}` : ""}</div>
            <a href={pdf} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#0A2540] font-medium hover:underline" data-testid="approval-pdf"><FileText className="h-4 w-4" /> Zobrazit zápis s fotodokumentací (PDF)</a>
            {rep.proposal && (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
                <div className="text-sm font-semibold text-amber-800">Zjištěna mechanická závada – návrh výměny dílů (vícepráce)</div>
                <ExtraItemsList rep={rep} />
                <p className="text-xs text-slate-600">Výměna se provede až po vašem odsouhlasení. Fotky poškození najdete v PDF zápisu.</p>
                {rep.proposal_status === "pending" ? (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="signer" className="text-xs text-slate-600">Jméno a příjmení</Label>
                      <Input id="signer" value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="např. Eva Dvořáková" className="bg-white h-10" data-testid="approval-signer" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-600 flex items-center gap-1"><PenLine className="h-3 w-3" /> Podpis předsedy (povinný pro schválení)</Label>
                      <SignaturePad onChange={setSignature} testId="approval-signature" />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => decide("approved")} disabled={busy || !signature} className="h-11 flex-1" style={{ background: "#065F46" }} autoFocus={preset === "approved"} data-testid="approval-approve"><CheckCircle2 className="h-4 w-4 mr-1" /> Podepsat a schválit</Button>
                      <Button onClick={() => decide("rejected")} disabled={busy} variant="outline" className="h-11 flex-1 text-red-700 border-red-300" data-testid="approval-reject"><XCircle className="h-4 w-4 mr-1" /> Zamítnout</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className={`font-semibold ${rep.proposal_status === "approved" ? "text-emerald-700" : "text-red-700"}`} data-testid="approval-status">
                      {rep.proposal_status === "approved" ? "Vícepráce schváleny – děkujeme." : "Návrh zamítnut."}
                    </p>
                    {rep.signed && <p className="text-xs text-slate-500" data-testid="approval-signed">Podepsáno{rep.signer_name ? `: ${rep.signer_name}` : ""} · podpis je součástí PDF zápisu.</p>}
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-slate-400">Rozhodnutí se zaznamená do systému OKENFIX a do pátečního podkladu pro fakturaci.</p>
          </>
        )}
      </div>
    </div>
  );
}
